import React, { useRef, useState } from 'react';
import { useIdioma } from '../../i18n/idioma';
import { useEstimates } from '../../contexts/EstimatesContext';
import { EstimateItem, EstimateItemType, EstimateAdjustments, ProposalSettings, SiteConditions, EstimateZone } from '../../types';
import SmartCalculators from './SmartCalculators';
import SmartTemplates from './SmartTemplates';
import AddMaterialModal from './AddMaterialModal';
import {
    Plus, Package,
    Briefcase, Truck, Trash2, Wrench, DollarSign,
    Percent, TrendingUp, TrendingDown, Target, Minus,
    MapPin, Wallet, Check, X
} from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatMarginPercent, marginPairOf } from '../../utils/formatMargin';
import type { MarginSolveResult } from '../../services/marginSolver';
import { priceLine, priceLineByType } from '../../utils/lineItemPricing';
import { lineTypeFromCatalogKind } from '../../utils/estimateLineTypes';
import { lookupCatalogItem } from '../../data/categories/catalogLoader';
import { laborCostPerHourFromSnapshot } from '../../services/estimateMath';
import { ClampedNumberInput } from '../../utils/numberInput';
import { Button } from '../ui/Button';
import { SearchInput } from '../ui/SearchInput';
import { SegmentedControl, SegmentOption } from '../ui/SegmentedControl';
import { EmptyState } from '../ui/EmptyState';
import { StatusDot } from '../ui/StatusDot';

interface EstimateToolPanelProps {
    items: EstimateItem[];
    onAddItem: (item: EstimateItem) => void;
    /** Drop a template's items into the estimate. If `targetZoneId` is omitted,
     *  a fresh zone is auto-created with the template's name and stamped with
     *  `sourceTemplateId` so the Save flow can detect template-derived zones. */
    onAddTemplateItems: (items: EstimateItem[], template: { id: string; name: string }, targetZoneId?: string) => void;
    adjustments: EstimateAdjustments;
    setAdjustments: (adj: EstimateAdjustments) => void;
    proposalSettings: ProposalSettings;
    setProposalSettings: React.Dispatch<React.SetStateAction<ProposalSettings>>;
    totals: any;
    snapshot: any;
    settings: any;
    /* `applyMargin` y `targetMarginGoal` ESTUVIERON aquí y se fueron en la ola 4.
     * Re-cotizar las partidas que ya están escritas es ahora un botón explícito
     * del panel de política de precios (components/EstimateCalculator.tsx), y
     * tener la función al alcance del componente del deslizador es exactamente
     * lo que permitió engancharla al gesto durante meses. No las devuelvas. */
    /** ¿Este estimado sigue admitiendo cambios de precio?
     *
     *  Un Sent / Accepted / Paid es un documento que el cliente YA VIO: el
     *  deslizador queda en solo lectura con su motivo a la vista (decisión 7).
     *  El criterio no se decide aquí —llega calculado por `canEditPricing`, el
     *  mismo que usa el panel del snapshot— para que no existan dos respuestas a
     *  «¿este estimado está cerrado?». Por omisión, editable: un panel montado
     *  sin este dato pertenece a un presupuesto que se está armando. */
    preciosEditables?: boolean;
    /* ── El dial de margen neto (Parte A.4) ──────────────────────────────────
     * Cuatro datos, todos del hook: el factor vigente, lo que el contratista
     * PIDIÓ, cómo fue el último intento de resolverlo, y la puerta para pedir
     * otro. El panel no calcula ninguno — solo los enseña y los pide. */
    /** El factor `k` vigente. 1 = «exactamente mis diales». */
    netMarginFactor: number;
    /** El margen que el contratista PIDIÓ sobre su trabajo propio, o null. */
    netMarginTarget: number | null;
    /** El último intento de resolver, con su `reasonCode` si fracasó. */
    netMarginSolve: MarginSolveResult | null;
    /** Pide un margen neto. Solo mueve precios si el solve es viable. */
    setNetMarginTarget: (percent: number | null) => MarginSolveResult | null;
    siteConditions: SiteConditions;
    zones: EstimateZone[];
    /** Create a zone by name and return its id (B-2). Lets the Materials tab add
     *  a zone right beside the "add items to zone" dropdown, including when no
     *  zones exist yet. Routed to the builder's single createNamedZone handler. */
    onCreateZone?: (name: string) => string;
    initialTradeId?: string;
    initialScopeCategoryId?: string;
    businessState?: string;
    onFocusUnpriced?: () => void;
}

type Tab = 'Templates' | 'Library' | 'SmartCalc' | 'Analysis';

/** Lo poco que hace falta de una fila de la lista de precios para saber QUÉ es. */
export interface LibraryRowOrigin {
    /** `pricebook.catalog_item_id` — la llave que une la fila del taller con el catálogo global. */
    catalogItemId?: string | null;
    /** `pricebook.category`, el respaldo cuando la fila no está enlazada al catálogo. */
    category?: string | null;
}

/**
 * El tipo de partida que le toca a una fila de la Biblioteca.
 *
 * POR QUÉ EXISTE (Parte B, ola 1, tarea 1-B del plan de precios v2):
 *   Esta función es la respuesta a `type: 'Material'` escrito a pelo. La
 *   Biblioteca no es una lista de materiales: 58 de sus filas enlazadas al
 *   catálogo son `equipment_rental` y hay permisos entre ellas. Cuando el tipo
 *   se teclea, la REGLA de precio se teclea con él —Material va por markup
 *   sobre el COSTO, Equipment por margen sobre el PRECIO, Fee a costo pelado—
 *   y una excavadora de $800/día salía a $960 (markup de material, 20%) en vez
 *   de a $1.142,86 (margen de equipo, 30%). $182,86 por día regalados, todos
 *   los días, sin que apareciera ningún número raro en pantalla. Y un permiso
 *   de $400 salía a $480: markup encima del recibo de un tercero que el cliente
 *   puede leer.
 *
 * DE DÓNDE SALE EL DATO, en este orden:
 *   1. La fila del CATÁLOGO, si la del taller está enlazada (`catalogItemId`).
 *      Es la fuente autorizada: trae `default_kind` y su propia categoría.
 *   2. Si no resuelve, la categoría de la fila del taller. Sirve para el pase
 *      directo: `upsertWorkspaceCatalogPrice` copia la categoría del catálogo
 *      al crear la fila, así que un permiso conserva 'permits-and-fees' aunque
 *      el enlace se haya perdido.
 *   3. Sin nada de eso, `lineTypeFromCatalogKind` devuelve 'Material', que es
 *      exactamente lo que la app hacía antes: una fila escrita a mano por el
 *      contratista no cambia de precio por este cambio.
 *
 * La traducción kind→tipo NO se decide aquí: vive entera en
 * utils/estimateLineTypes, incluida la razón por la que el kind de equipo le
 * gana a la categoría de permisos y la categoría de permisos le gana al kind
 * 'material' con el que el catálogo etiqueta sus 44 filas de permisos.
 */
export function libraryRowLineType(pbItem: LibraryRowOrigin | null | undefined): EstimateItemType {
    const catalogItem = pbItem?.catalogItemId ? lookupCatalogItem(pbItem.catalogItemId) : null;
    return lineTypeFromCatalogKind(
        catalogItem?.default_kind,
        catalogItem?.category ?? pbItem?.category,
    );
}

const EstimateToolPanel: React.FC<EstimateToolPanelProps> = ({
    items, onAddItem, onAddTemplateItems, adjustments, setAdjustments, proposalSettings, setProposalSettings, totals, snapshot, settings, preciosEditables = true, netMarginFactor, netMarginTarget, netMarginSolve, setNetMarginTarget, siteConditions, zones, onCreateZone, initialTradeId, initialScopeCategoryId, businessState, onFocusUnpriced
}) => {
    const { t } = useIdioma();
    const { pricebook } = useEstimates();
    /* Arranca en Materiales, salvo que el estimado venga de «Empezar un trabajo»
     * con un alcance ya escogido: en ese caso el contratista YA eligió servicio,
     * la pestaña de Plantillas trae ese alcance filtrado y su propio aviso, y
     * soltarlo en Materiales tiraría ese contexto.
     *
     * (El ternario que había aquí devolvía 'Templates' por las dos ramas.) */
    const [activeTab, setActiveTab] = useState<Tab>(initialScopeCategoryId ? 'Templates' : 'Library');

    // -- TAB STATE: LIBRARY --
    const [search, setSearch] = useState('');
    /** The zone all Library quick-adds & pricebook adds will land into.
     *  Empty string means "no zone" (item shows in Unassigned). */
    const [addToZoneId, setAddToZoneId] = useState<string>('');
    const resolveZoneId = () => addToZoneId || undefined;
    const [addMaterialOpen, setAddMaterialOpen] = useState(false);
    // B-2: inline "+ Add zone" in the Materials tab, beside the destination select.
    const [addingZone, setAddingZone] = useState(false);
    const [zoneNameDraft, setZoneNameDraft] = useState('');
    const confirmAddZone = () => {
        const name = zoneNameDraft.trim();
        if (name && onCreateZone) {
            // Create the zone and immediately target it so the next add lands there.
            setAddToZoneId(onCreateZone(name));
        }
        setZoneNameDraft('');
        setAddingZone(false);
    };

    // --- ACTIONS ---

    /* La POLÍTICA DE PRECIOS DE ESTE ESTIMADO, en un solo objeto para las tres
     * altas de abajo y para los tres hijos que cotizan (plantillas, calculadoras
     * y el modal de material).
     *
     * UNA SOLA FUENTE, SIEMPRE. Antes cada alta elegía la suya: el material leía
     * el dial vivo y la mano de obra el snapshot. Con el mismo break-even de
     * $51,77, «Agregar mano de obra» cotizaba a $64,71/h (margen viejo, 20%) y
     * soltar una plantilla a $73,95/h (margen vivo, 30%): dos filas de labor
     * idénticas con $9,24/h de diferencia en el mismo documento.
     *
     * Y ESA FUENTE ES EL SNAPSHOT, no `settings.pricing` (decisión 2, ola 4).
     * Los diales del taller son el valor por defecto con el que NACE un
     * estimado; una vez nacido, la política es suya y la edita el panel «Precio
     * rápido» de la cabecera. Leyendo los ajustes vivos, una partida agregada
     * hoy se cotizaría con una política distinta a la de sus vecinas de ayer sin
     * que nada en pantalla lo dijera. La caída a `settings` es para los
     * estimados guardados antes de que el snapshot llevara estos campos. */
    const dials = {
        targetMarginPercent: Number.isFinite(snapshot?.targetMarginPercent)
            ? snapshot.targetMarginPercent
            : settings.pricing.targetMarginPercent,
        materialMarkupPercent: Number.isFinite(snapshot?.materialMarkupPercent)
            ? snapshot.materialMarkupPercent
            : settings.pricing.defaultMaterialMarkupPercent,
    };

    /* Alta desde la Biblioteca. Se llamaba `handleAddMaterial`, y el nombre era
     * la mitad del fallo: por aquí entran materiales, sí, pero también rentas de
     * equipo y permisos, y todos salían cotizados como material. */
    const handleAddFromLibrary = (pbItem: any) => {
        const cost = pbItem.cost || 0;
        // El tipo lo dice el ORIGEN de la fila, no esta línea de código.
        const tipo = libraryRowLineType(pbItem);

        // El markup propio de la fila del pricebook gana cuando existe —un
        // repuesto de especialidad se cobra al 60% y no al dial general— y si
        // no, manda el dial del taller. La regla de conversión vive en
        // utils/lineItemPricing.
        //
        // Solo se aplica a los MATERIALES, y no por gusto: lo guardado es un
        // markup, un porcentaje sobre el COSTO. Equipment y Subcontractor se
        // cotizan por margen, sobre el PRECIO, y meter ese número en la fórmula
        // del margen sería sumar peras con manzanas: un 42,857% guardado —que es
        // justo lo que un margen del 30% deja escrito en la fila del equipo— se
        // leería como «margen del 42,857%» y multiplicaría el precio por 1,75 en
        // vez de por 1,43. Un Fee no admite porcentaje ninguno.
        //
        // NULO Y CERO NO SON LO MISMO, y esta es la línea donde se decide:
        //   · `null`  → «esta fila no opina; sigue el dial del taller». Es lo
        //               que deja la ola 1-A al dejar de escribir porcentajes
        //               derivados en el pricebook, y lo que trae toda fila
        //               nacida con `markup_source = 'dial'`.
        //   · `0`     → «este material se cobra A COSTO». Es una decisión de la
        //               persona, tan válida como teclear 60, y hasta ahora se
        //               perdía: la condición pedía `> 0`, así que un cero
        //               explícito caía al dial y el cliente pagaba un 20% que
        //               nadie había pedido cobrarle.
        // Por eso basta con que el valor SEA un número; el `??` de abajo sabe
        // distinguir el 0 del nulo, cosa que un `||` no haría.
        //
        // La PROCEDENCIA ya no se infiere: se lee. La migración
        // 20260810060209 añadió `markup_source`, y con ella un markup
        // `'derived'` —los 68 que en realidad eran un margen reescrito, como el
        // 42,857% que arrancó esta auditoría— se IGNORA y manda el dial del
        // taller. Eso es, literalmente, el fallo que se reportó: «agregué un
        // material y me puso 43% en vez del 20% que tengo configurado».
        //
        // Lo que NO se hace todavía es exigir `=== 'user'`. Sería tentador
        // —«solo gana lo que una persona tecleó»— pero hoy CERO filas están
        // selladas 'user': la migración las dejó todas en 'dial' porque no
        // había forma de saber cuáles se tecleraron a mano. Exigirlo ahora
        // tiraría también los markups deliberados de 17 talleres. A medida que
        // la gente edite sus precios, el modal los irá sellando 'user' y esta
        // condición podrá endurecerse sin llevarse nada por delante.
        const markupContaminado = pbItem.markupSource === 'derived';
        const savedMarkup = (tipo === 'Material' && !markupContaminado
                             && typeof pbItem.markupPercent === 'number' && Number.isFinite(pbItem.markupPercent))
            ? pbItem.markupPercent
            : null;
        const { rate, markupPercent, marginPercent } = priceLineByType(tipo, cost, {
            ...dials,
            materialMarkupPercent: savedMarkup ?? dials.materialMarkupPercent,
        });

        const newItem: EstimateItem = {
            id: Math.random().toString(36).substr(2, 9),
            type: tipo,
            description: pbItem.name,
            quantity: 1,
            cost: cost,
            markupPercent,
            marginPercent,
            rate,
            // Nace con k = 1 —«exactamente mis diales»—, así que su precio base
            // y su precio son el mismo número HOY. Guardarlo igualmente es lo
            // que permite que el dial de margen neto la mueva mañana sin
            // reescribirse encima de sí misma (Parte A.1 y A.6.6): una fila
            // agregada después de un solve se cotiza sobre el mismo pie que una
            // idéntica agregada antes, porque las dos conservan de dónde salen.
            baseRate: rate,
            total: rate,
            sourcePricebookId: pbItem.id,
            unit: pbItem.unit || 'EA',
            zoneId: resolveZoneId(),
            isOverridden: false
        };
        onAddItem(newItem);
    };

    const handleAddLabor = () => {
        const laborCost = laborCostPerHourFromSnapshot(snapshot);
        const { rate, markupPercent, marginPercent } = priceLineByType('Labor', laborCost, dials);
        // Sin costo de mano de obra no hay precio que derivar; la tarifa
        // objetivo del snapshot es el único número disponible.
        const targetRate = laborCost > 0 ? rate : snapshot.targetHourlyRate;

        const newItem: EstimateItem = {
            id: Math.random().toString(36).substr(2, 9),
            type: 'Labor',
            description: t('herrEst.item.manoDeObraGeneral'),
            quantity: 1,
            cost: laborCost,
            markupPercent,
            marginPercent,
            rate: targetRate,
            // El precio base es el mismo que el precio: la fila nace con k = 1.
            // Ojo con el caso sin costo: ahí `targetRate` NO viene de la regla
            // sino de la tarifa objetivo del snapshot, y aun así es la línea
            // base correcta —es el único precio que esta fila tiene—, así que
            // el dial la escalará desde ahí y no desde un 0 que la borraría.
            baseRate: targetRate,
            total: targetRate,
            unit: 'HR',
            zoneId: resolveZoneId(),
            isOverridden: false
        };
        onAddItem(newItem);
    };

    const handleQuickAdd = (desc: string, type: 'Material' | 'Equipment' | 'Fee', cost: number, unit: string = 'EA') => {
        // Cada tipo por su regla: el tiradero y la entrega son 'Fee' y se
        // remiten a costo —antes entraban con margen, y el primer roce del
        // deslizador se lo quitaba solo, así que el precio del recibo cambiaba
        // sin que nadie lo tocara.
        const { rate, total, markupPercent, marginPercent } = priceLine(type, cost, 1, dials);
        const newItem: EstimateItem = {
            id: Math.random().toString(36).substr(2, 9),
            type,
            description: desc,
            quantity: 1,
            cost,
            markupPercent,
            marginPercent,
            rate,
            // Nace con k = 1, así que baseRate y rate coinciden. En los Fee el
            // valor es el costo pelado y ahí se queda: `applyFactor` no escala
            // el pase directo, de modo que guardar su base no le abre la puerta
            // al dial —solo deja la fila descrita con la misma forma que todas.
            baseRate: rate,
            total,
            unit,
            zoneId: resolveZoneId(),
            isOverridden: false
        };
        onAddItem(newItem);
    };

    // --- Donut chart helper ---
    const DonutChart = ({ segments }: { segments: { value: number; color: string; label: string }[] }) => {
        const total = segments.reduce((s, seg) => s + seg.value, 0);
        if (total === 0) return null;
        let cumPercent = 0;
        const radius = 52;
        const circumference = 2 * Math.PI * radius;

        return (
            <div className="flex items-center gap-6">
                <div className="relative w-[140px] h-[140px] shrink-0">
                    <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                        {segments.filter(s => s.value > 0).map((seg, i) => {
                            const pct = seg.value / total;
                            const dashLen = circumference * pct;
                            const dashOff = circumference * cumPercent;
                            cumPercent += pct;
                            return (
                                <circle
                                    key={i}
                                    cx="60" cy="60" r={radius}
                                    fill="none"
                                    stroke={seg.color}
                                    strokeWidth="14"
                                    strokeDasharray={`${dashLen} ${circumference - dashLen}`}
                                    strokeDashoffset={-dashOff}
                                    strokeLinecap="round"
                                    className="transition-[stroke-dasharray,stroke-dashoffset] duration-200"
                                />
                            );
                        })}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="label-ui">{t('herrEst.grafica.centro')}</span>
                    </div>
                </div>
                <div className="space-y-2.5 flex-1 min-w-0">
                    {segments.filter(s => s.value > 0).map((seg, i) => (
                        <div key={i} className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                                <span className="text-[13px] text-acero-600 truncate">{seg.label}</span>
                            </div>
                            <span className="text-[13px] font-data tabular-nums text-navy-950 shrink-0">{formatCurrency(seg.value)}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    /* ═══ EL DIAL DE MARGEN NETO (Parte A.4) ═══════════════════════════════════
     *
     * QUÉ PROMETÍA Y QUÉ ENTREGABA. El número grande decía «margen neto» y el
     * deslizador llamaba a `applyMargin(valor)` en CADA `onChange`, o sea «pon el
     * margen de cada partida en X» — que es otra cosa. Medido con los diales
     * 30/20: el deslizador en 30 daba un agregado real del 23,91%; no era
     * monótono (posición 11 → 6,26%; posición 10 → 13,46%); y el resultado
     * dependía del GESTO, porque cada paso intermedio del arrastre re-cotizaba
     * encima de lo ya re-cotizado (clic al 10 → 13,46%; arrastrar al 10 → 5,71%:
     * 7,75 puntos de diferencia por mover el ratón distinto).
     *
     * LAS DOS DECISIONES QUE LO ARREGLAN:
     *
     *   1. `onChange` NO aplica nada. Solo mueve el pulgar y el número que se
     *      pide. Se aplica UNA vez al soltar (`onPointerUp` / `onKeyUp` /
     *      `onBlur`). Un gesto, una operación: la dependencia del camino
     *      desaparece de raíz en vez de mitigarse.
     *   2. Lo que se aplica es un FACTOR ABSOLUTO desde `baseRate`, no un
     *      incremento. Por eso «deshacer» sale gratis: volver a arrastrar al
     *      valor anterior devuelve las tarifas al centavo, porque se recalcula
     *      desde la misma base y no desde el precio ya escalado.
     *
     * `pedidoLocal` es la posición del pulgar mientras se arrastra Y después de
     * soltar. No se limpia al aplicar a propósito: cuando el solve NO es viable
     * los precios se quedan donde estaban, y el pulgar tiene que seguir marcando
     * lo que la persona pidió para que el aviso de abajo tenga a qué referirse.
     * Un pulgar que salta solo a su sitio anterior es un dial que se traga la
     * pregunta. */
    const [pedidoLocal, setPedidoLocal] = useState<number | null>(null);
    /* ¿Este gesto movió el deslizador? En un ref y no en estado: cambia dentro
     * del propio manejador y no debe provocar un render. Sirve para que un
     * `onBlur` o un `onKeyUp` sueltos —tabular sobre el control, por ejemplo— no
     * re-apliquen un solve que ya se aplicó. */
    const gestoPendiente = useRef(false);

    const margenReal = Number.isFinite(totals.marginPercentOwnWork) ? totals.marginPercentOwnWork : 0;
    /* La posición del pulgar, por orden de autoridad: lo que la persona acaba de
     * pedir, lo que este estimado tiene guardado como objetivo, y si no hay ni
     * una cosa ni la otra, el margen que el estimado da hoy — que es el ancla
     * honesta: el dial arranca donde está el trabajo, no en un número inventado. */
    const posicionDial = Math.max(0, Math.min(60, Math.round(pedidoLocal ?? netMarginTarget ?? margenReal)));

    const moverDial = (valor: number) => {
        /* En un estimado cerrado el control ya viene `disabled`, así que esto no
         * debería llegar nunca. Se comprueba igual: un `disabled` es una promesa
         * de la pantalla, y la promesa de que el precio no se mueve tiene que
         * vivir también donde se mueve el precio. */
        if (!preciosEditables) return;
        gestoPendiente.current = true;
        setPedidoLocal(valor);
    };

    /** Soltar = aplicar, una sola vez, y solo si el gesto movió algo. */
    const soltarDial = () => {
        if (!preciosEditables) return;
        if (!gestoPendiente.current) return;
        gestoPendiente.current = false;
        if (pedidoLocal === null) return;
        setNetMarginTarget(pedidoLocal);
    };

    /* Las dos lecturas del margen, del formateador compartido: un decimal
     * siempre, y el facturado solo cuando difiere del propio (A.5). Aquí estaba
     * el `.toFixed(0)` que convertía 29,5% en un «30%» que parecía cumplir la
     * meta. */
    const par = marginPairOf(totals);

    /* ¿En meta? Contra el objetivo DEL ESTIMADO, nunca contra
     * `settings.pricing.targetMarginPercent`: ese es el dial de la MANO DE OBRA
     * —el porcentaje con el que se cotiza una fila de labor— y compararlo con un
     * agregado que incluye materiales, viaje y descuento es mezclar dos cosas
     * distintas. Sin objetivo del estimado no hay veredicto: `null`, y la
     * insignia no sale. Inventar uno prestado es cómo el número grande acabó
     * pintado de rojo en presupuestos que nadie había puesto en meta.
     *
     * El margen de 0,05 es medio dígito de lo que se PINTA: así la insignia
     * nunca contradice al número de al lado por polvo de coma flotante. */
    const enMeta = netMarginTarget === null ? null : margenReal >= netMarginTarget - 0.05;

    /** El solve falló: se explica el motivo y NO se movió ningún precio. */
    const solveFallido = netMarginSolve !== null && !netMarginSolve.feasible;

    /** El motivo, traducido. El código es estable; el texto es de la interfaz. */
    const motivoDelSolve = (resultado: MarginSolveResult): string => {
        switch (resultado.reasonCode) {
            case 'minimo-de-servicio':
                return t('herrEst.margen.motivo.minimoDeServicio');
            case 'sin-trabajo-propio':
                return t('herrEst.margen.motivo.sinTrabajoPropio');
            case 'objetivo-invalido':
                return t('herrEst.margen.motivo.objetivoInvalido');
            case 'margen-independiente-de-k':
                return t('herrEst.margen.motivo.margenIndependiente');
            case 'techo-inalcanzable':
                return t('herrEst.margen.motivo.techoInalcanzable', { techo: formatMarginPercent(resultado.ceiling) });
            case 'fuera-de-rango':
                return t('herrEst.margen.motivo.fueraDeRango');
            default:
                return t('herrEst.margen.motivo.codo');
        }
    };

    /* Objetivo guardado que ya no coincide con la realidad: pasa al agregar una
     * partida DESPUÉS de resolver, porque añadir no re-resuelve solo (decisión
     * 6). Se enseñan los dos números en vez de mentir con uno. */
    const objetivoDesfasado = !solveFallido
        && netMarginTarget !== null
        && formatMarginPercent(netMarginTarget) !== par.own;

    /* El factor vigente, para que el nivel de precios sea legible: con k = 1 no
     * se enseña nada —«exactamente mis diales» no necesita chip—. */
    const factorVisible = Number.isFinite(netMarginFactor) && Math.abs(netMarginFactor - 1) > 0.0005
        ? netMarginFactor
        : null;

    /* Orden pedido por el equipo: Materiales primero. Es la pestaña que se
     * abre sola, así que es lo primero que ve el contratista.
     *
     * «Calc» era el único rótulo escrito a mano en inglés — no pasaba por t(),
     * así que se quedaba en «Calc» aunque la app estuviera en español. */
    const tabOptions: SegmentOption<Tab>[] = [
        { value: 'Library', label: t('estimado.tab.materiales') },
        { value: 'Templates', label: t('estimado.tab.servicios') },
        { value: 'SmartCalc', label: t('estimado.tab.calculadoras'), count: items.length || undefined },
        { value: 'Analysis', label: t('estimado.tab.precios') },
    ];

    // --- RENDER CONTENT ---

    return (
        <div className="w-full lg:w-[640px] bg-white lg:border-l border-acero-200 flex flex-col h-full min-h-0 shrink-0 relative z-10">

            {/* TABS — Materiales primero (ver tabOptions) */}
            <div className="px-5 pt-4 bg-white shrink-0">
                <SegmentedControl
                    variant="underline"
                    options={tabOptions}
                    value={activeTab}
                    onChange={(v) => setActiveTab(v)}
                />
            </div>

            {/* CONTENT AREA — single scroll context per tab */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">

                {/* 1. SERVICES (templates) */}
                {activeTab === 'Templates' && (
                    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                        {initialScopeCategoryId && items.length === 0 && (
                            <div className="shrink-0 mx-4 mt-3 px-3 py-2 border border-brand-200 rounded-lg text-[11px] text-navy-950">
                                {t('estimado.pista.escoge')} <span className="font-semibold">{t('estimado.pista.agregar')}</span> {t('estimado.pista.automatico')}
                            </div>
                        )}
                        <SmartTemplates
                            onAddTemplateItems={onAddTemplateItems}
                            targetMarginPercent={dials.targetMarginPercent}
                            materialMarkupPercent={dials.materialMarkupPercent}
                            zones={zones}
                            laborOverhead={{
                                baseLaborCost: snapshot.baseLaborCost || 0,
                                laborBurdenPercent: snapshot.laborBurdenPercent || 0,
                                overheadPerManHour: snapshot.overheadPerManHour || 0,
                                breakEvenRate: snapshot.breakEvenRate,
                            }}
                            onRequestCalculator={() => setActiveTab('SmartCalc')}
                            initialTradeId={initialTradeId}
                            initialScopeCategoryId={initialScopeCategoryId}
                            existingItems={items}
                            businessState={businessState}
                            onFocusUnpriced={onFocusUnpriced}
                        />
                    </div>
                )}

                {/* 2. MATERIALS (library) */}
                {activeTab === 'Library' && (
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain custom-scrollbar pb-40 lg:pb-10">
                    <div className="space-y-5 animate-in fade-in slide-in-from-right-4 p-6">

                        {/* Destination zone — always shown (B-2) so a fresh estimate
                            with zero zones can create its first zone right here,
                            beside the "add items to zone" selector. */}
                        <div className="bg-white border border-acero-200 rounded-2xl p-4">
                            <label className="flex items-center gap-2 mb-1.5">
                                <MapPin size={12} className="text-acero-400" />
                                <span className="label-ui">{t('herrEst.zona.destino')}</span>
                            </label>
                            <div className="flex items-center gap-2">
                                <select
                                    value={addToZoneId}
                                    onChange={(e) => setAddToZoneId(e.target.value)}
                                    className="flex-1 min-w-0 h-10 text-sm bg-white border border-acero-300 rounded-xl px-3 text-navy-950 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                >
                                    <option value="">{t('herrEst.zona.sinZona')}</option>
                                    {zones.map(z => (
                                        <option key={z.id} value={z.id}>{z.name}</option>
                                    ))}
                                </select>
                                {onCreateZone && !addingZone && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="shrink-0"
                                        onClick={() => { setZoneNameDraft(''); setAddingZone(true); }}
                                        leftIcon={<Plus size={14} />}
                                    >
                                      {t('estimado.zona.agregar')}
                                    </Button>
                                )}
                            </div>
                            {onCreateZone && addingZone && (
                                <div className="flex items-center gap-1 mt-2">
                                    <input
                                        type="text"
                                        autoFocus
                                        value={zoneNameDraft}
                                        onChange={(e) => setZoneNameDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') confirmAddZone();
                                            if (e.key === 'Escape') { setZoneNameDraft(''); setAddingZone(false); }
                                        }}
                                        placeholder={t('herrEst.zona.nombrePlaceholder')}
                                        className="flex-1 min-w-0 h-10 px-3 text-sm bg-white border border-acero-300 text-navy-950 rounded-xl transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                    />
                                    <button onClick={confirmAddZone} aria-label={t('estimado.zona.confirmar')} className="shrink-0 p-2 text-navy-700 hover:bg-acero-100 hover:text-navy-950 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                                        <Check size={14} />
                                    </button>
                                    <button onClick={() => { setZoneNameDraft(''); setAddingZone(false); }} aria-label={t('estimado.zona.cancelar')} className="shrink-0 p-2 text-acero-500 hover:bg-acero-100 hover:text-navy-950 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                                        <X size={14} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Quick Add Buttons */}
                        <div className="space-y-3">
                            <h4 className="label-ui">{t('herrEst.rapido.titulo')}</h4>
                            <div className="grid grid-cols-2 gap-2">
                                <Button variant="secondary" onClick={() => setAddMaterialOpen(true)} leftIcon={<Plus size={14} />}>
                                    Material
                                </Button>
                                <Button variant="outline" onClick={handleAddLabor} leftIcon={<Briefcase size={14} />}>
                                    Labor
                                </Button>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <Button variant="outline" size="sm" className="!h-auto py-2.5 flex-col gap-1" onClick={() => handleQuickAdd(t('estimado.rapido.dumpfee'), 'Fee', 85, 'LOAD')}>
                                    <Trash2 size={13} />Dump
                                </Button>
                                <Button variant="outline" size="sm" className="!h-auto py-2.5 flex-col gap-1" onClick={() => handleQuickAdd(t('estimado.rapido.entrega'), 'Fee', 95, 'EA')}>
                                    <Truck size={13} />{t('estimado.rapido.entregaBoton')}
                                </Button>
                                <Button variant="outline" size="sm" className="!h-auto py-2.5 flex-col gap-1" onClick={() => handleQuickAdd(t('estimado.rapido.rentaequipo'), 'Equipment', 150, 'DAY')}>
                                    <Wrench size={13} />{t('estimado.rapido.rentaBoton')}
                                </Button>
                            </div>
                        </div>

                        {/* Search */}
                        <SearchInput
                            className="w-full"
                            value={search}
                            onChange={setSearch}
                            placeholder={t('herrEst.buscar.placeholder')}
                        />

                        {/* Pricebook List */}
                        <div className="space-y-2">
                            {pricebook.filter(p => p.name.toLowerCase().includes(search.toLowerCase())).slice(0, 20).map(item => (
                                <button
                                    key={item.id}
                                    onClick={() => handleAddFromLibrary(item)}
                                    className="w-full text-left p-3 bg-white border border-acero-200 rounded-xl hover:border-acero-300 transition-colors duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                >
                                    <div className="flex justify-between items-start gap-3">
                                        <span className="font-medium text-navy-950 text-sm leading-tight">{item.name}</span>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {item.unit && (
                                                <span className="label-ui">{item.unit}</span>
                                            )}
                                            <span className="text-[13px] font-data tabular-nums font-medium text-navy-950">${item.cost}</span>
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <StatusDot tone="neutro">{item.category}</StatusDot>
                                    </div>
                                </button>
                            ))}
                            {pricebook.length === 0 && (
                                <EmptyState
                                    icon={<Package size={18} />}
                                    title={t('herrEst.vacio.titulo')}
                                    description={t('herrEst.vacio.texto')}
                                    action={<Button variant="secondary" onClick={() => setAddMaterialOpen(true)} leftIcon={<Plus size={14} />}>{t('estimado.vacio.agregarmaterial')}</Button>}
                                />
                            )}
                        </div>
                    </div>
                    </div>
                )}

                {/* Add Material modal — invoked from the Library tab. Saves to
                    workspace pricebook AND drops into the currently-selected
                    zone (resolved via the Library's zone dropdown). */}
                <AddMaterialModal
                    isOpen={addMaterialOpen}
                    targetMarginPercent={dials.targetMarginPercent}
                    materialMarkupPercent={dials.materialMarkupPercent}
                    onClose={() => setAddMaterialOpen(false)}
                    onAdded={(item) => {
                        const withZone = { ...item, zoneId: resolveZoneId() };
                        onAddItem(withZone);
                    }}
                />

                {/* 3. CALCULATORS */}
                {activeTab === 'SmartCalc' && (
                    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    <SmartCalculators
                        onAddTemplateItems={onAddTemplateItems}
                        targetMarginPercent={dials.targetMarginPercent}
                        materialMarkupPercent={dials.materialMarkupPercent}
                        activeTradeId={initialTradeId}
                    />
                    </div>
                )}

                {/* 4. PRICING & ADJUSTMENTS */}
                {activeTab === 'Analysis' && (
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain custom-scrollbar pb-40 lg:pb-10">
                    <div className="space-y-5 animate-in fade-in slide-in-from-right-4 p-6">

                        {/* Profit Margin Control Card — el dial de margen neto (A.4).
                            El número grande es el margen REAL del trabajo propio; el
                            deslizador es lo que se PIDE. Nunca al revés. */}
                        <div className="p-6 rounded-2xl border border-acero-200">
                            <div className="flex justify-between items-end mb-5">
                                <div>
                                    <div className="flex items-center gap-3 mb-1.5">
                                        <p className="label-ui">{t('herrEst.margen.titulo')}</p>
                                        {enMeta === false && (
                                            <StatusDot tone="aviso">
                                                <span className="inline-flex items-center gap-1"><TrendingDown size={11} /> {t('estimado.margen.bajometa')}</span>
                                            </StatusDot>
                                        )}
                                        {enMeta === true && (
                                            <StatusDot tone="exito">
                                                <span className="inline-flex items-center gap-1"><TrendingUp size={11} /> {t('estimado.margen.enmeta')}</span>
                                            </StatusDot>
                                        )}
                                        {factorVisible !== null && (
                                            <StatusDot tone="neutro">
                                                <span className="font-data tabular-nums">×{factorVisible.toFixed(3)}</span>
                                            </StatusDot>
                                        )}
                                    </div>
                                    {/* Un solo nodo de texto, del formateador compartido: partir
                                        el «%» en su propio <span> obligaba a formatear a mano. */}
                                    <div className={`text-[34px] leading-none font-data font-semibold tabular-nums ${enMeta === false ? 'text-aviso' : 'text-navy-950'}`}>
                                        {par.own}
                                    </div>
                                    {par.differs && (
                                        <p className="mt-1.5 text-[11px] text-acero-500">
                                            <span>{t('herrEst.margen.propio')}</span>
                                            {' · '}
                                            <span className="font-data tabular-nums">{par.billed}</span>{' '}
                                            <span>{t('herrEst.margen.facturado')}</span>
                                        </p>
                                    )}
                                </div>
                                <div className="text-right">
                                    <p className="label-ui mb-1">{t('herrEst.margen.ganancia')}</p>
                                    <p className={`text-2xl font-data font-semibold tabular-nums ${enMeta === false ? 'text-aviso' : 'text-navy-950'}`}>{formatCurrency(totals.netProfit)}</p>
                                </div>
                            </div>

                            {/* Interactive Slider — arrastrar NO cotiza. Se aplica al soltar. */}
                            <div className="relative pt-1 pb-1">
                                <input
                                    type="range"
                                    min="0"
                                    max="60"
                                    step="1"
                                    value={posicionDial}
                                    disabled={!preciosEditables}
                                    onChange={(e) => moverDial(Number(e.target.value))}
                                    onPointerUp={soltarDial}
                                    onKeyUp={soltarDial}
                                    onBlur={soltarDial}
                                    aria-label={t('herrEst.margen.sliderAria')}
                                    className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-navy-850 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-50"
                                />
                                <div className="flex justify-between label-ui mt-2">
                                    <span className="font-data">0%</span>
                                    {/* La meta que marca el pulgar, no el dial de la mano de obra:
                                        mientras se arrastra este número es el único que se mueve. */}
                                    <span className="flex items-center gap-1">
                                        <Target size={11} /> {t('herrEst.margen.meta')}<span className="font-data tabular-nums">{posicionDial}%</span>)
                                    </span>
                                    <span className="font-data">60%</span>
                                </div>
                            </div>

                            {/* El documento ya salió al cliente (decisión 7). Un
                                deslizador apagado y sin explicación se lee como
                                una app rota; con el motivo al lado se lee como lo
                                que es: el precio quedó como el cliente lo vio. */}
                            {!preciosEditables && (
                                <p className="mt-4 px-3.5 py-2.5 rounded-xl bg-aviso-bg border border-aviso/25 text-xs text-aviso">
                                    {t('herrEst.margen.bloqueado')}
                                </p>
                            )}

                            {/* El solve no fue viable: se dice por qué y no se movió un precio.
                                Sin esto quedarían dos números contradictorios —el pulgar en 45
                                y el número grande en 23,9— sin una palabra que lo explique. */}
                            {solveFallido && netMarginSolve && (
                                <div className="mt-4 px-3.5 py-2.5 rounded-xl bg-aviso-bg border border-aviso/25">
                                    <p className="text-xs font-medium text-aviso">
                                        {t('herrEst.margen.pedido', { pedido: formatMarginPercent(posicionDial) })}
                                    </p>
                                    <p className="mt-1 text-[11px] text-navy-950">{motivoDelSolve(netMarginSolve)}</p>
                                </div>
                            )}

                            {/* Objetivo guardado que la realidad ya dejó atrás (decisión 6). */}
                            {objetivoDesfasado && (
                                <p className="mt-3 text-[11px] text-acero-500">
                                    {t('herrEst.margen.objetivoReal', {
                                        objetivo: formatMarginPercent(netMarginTarget),
                                        real: par.own,
                                    })}
                                </p>
                            )}
                        </div>

                        {/* Cost Breakdown Donut */}
                        <div className="bg-white border border-acero-200 rounded-2xl p-6">
                            <h4 className="label-ui mb-5">{t('herrEst.desglose.titulo')}</h4>
                            <DonutChart segments={[
                                { value: totals.laborCostTotal, color: '#1A3A6B', label: t('estimado.grafica.manodeobra') },
                                { value: totals.materialCostTotal, color: '#FACC15', label: t('estimado.grafica.materiales') },
                                { value: Math.max(totals.netProfit, 0), color: '#188A4C', label: t('estimado.grafica.ganancia') },
                            ]} />
                            <div className="border-t border-acero-200 mt-5 pt-4 flex justify-between text-navy-950 text-sm">
                                <span className="font-medium">{t('herrEst.desglose.total')}</span>
                                <span className="font-data tabular-nums font-semibold">{formatCurrency(totals.subTotal + totals.finalMinFee)}</span>
                            </div>
                        </div>

                        {/* Discount Card */}
                        <div className="bg-white border border-acero-200 rounded-2xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="label-ui">{t('herrEst.descuento.titulo')}</h4>
                                <div className="inline-flex items-center gap-0.5 bg-acero-100 rounded-xl p-0.5 h-10">
                                    <button
                                        onClick={() => setAdjustments({ ...adjustments, discountType: 'flat' })}
                                        aria-label={t('herrEst.descuento.dolaresAria')}
                                        className={`h-9 px-3.5 rounded-[10px] text-[13px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${adjustments.discountType === 'flat' ? 'bg-white text-navy-950 shadow-s1' : 'text-acero-600 hover:text-navy-950'}`}
                                    >
                                        <DollarSign size={13} />
                                    </button>
                                    <button
                                        onClick={() => setAdjustments({ ...adjustments, discountType: 'percent' })}
                                        aria-label={t('herrEst.descuento.porcentajeAria')}
                                        className={`h-9 px-3.5 rounded-[10px] text-[13px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${adjustments.discountType === 'percent' ? 'bg-white text-navy-950 shadow-s1' : 'text-acero-600 hover:text-navy-950'}`}
                                    >
                                        <Percent size={13} />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="relative flex-1">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-acero-400 text-xs pointer-events-none">
                                        {adjustments.discountType === 'flat' ? '$' : ''}
                                    </span>
                                    <input
                                        type="number"
                                        min="0"
                                        step={adjustments.discountType === 'percent' ? 1 : 5}
                                        className="w-full h-10 pl-7 pr-12 text-sm bg-white border border-acero-300 rounded-xl text-right font-data tabular-nums text-navy-950 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                        value={adjustments.discount || ''}
                                        placeholder="0"
                                        onChange={(e) => setAdjustments({ ...adjustments, discount: Number(e.target.value) || 0 })}
                                    />
                                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-acero-400 text-xs pointer-events-none">
                                        {adjustments.discountType === 'percent' ? '%' : ''}
                                    </span>
                                </div>
                                {adjustments.discount > 0 && (
                                    <span className="text-sm font-data tabular-nums font-medium text-peligro whitespace-nowrap inline-flex items-center gap-0.5">
                                        <Minus size={11} /> {formatCurrency(totals.discountAmount)}
                                    </span>
                                )}
                            </div>
                            {/* Show clamp warning when the user typed a discount > preDiscountSubtotal */}
                            {adjustments.discount > 0 && adjustments.discountType === 'flat' &&
                              adjustments.discount > (totals.subTotal + totals.discountAmount) && (
                                <p className="mt-2 text-xs text-aviso">
                                    {t('herrEst.descuento.pasaSubtotal')}<span className="font-data tabular-nums">{formatCurrency(totals.discountAmount)}</span>
                                </p>
                            )}
                        </div>

                        {/* Deposit Card */}
                        <div className="bg-white border border-acero-200 rounded-2xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="label-ui flex items-center gap-2">
                                    <Wallet size={12} /> Deposit
                                </h4>
                                <span className="text-sm font-data tabular-nums font-semibold text-navy-950">{formatCurrency(totals.depositAmount)}</span>
                            </div>
                            <div className="flex items-center gap-3 mb-2">
                                <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={5}
                                    value={adjustments.depositPercent ?? 0}
                                    onChange={(e) => setAdjustments({ ...adjustments, depositPercent: Number(e.target.value) })}
                                    aria-label={t('herrEst.anticipo.sliderAria')}
                                    className="flex-1 h-2 bg-acero-100 rounded-lg appearance-none cursor-pointer accent-navy-850 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                />
                                <div className="relative shrink-0">
                                    <ClampedNumberInput
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={adjustments.depositPercent ?? 0}
                                        onCommit={(v) => setAdjustments({ ...adjustments, depositPercent: v })}
                                        className="w-16 h-10 px-2 pr-6 text-sm bg-white border border-acero-300 rounded-xl text-right font-data tabular-nums text-navy-950 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-acero-400 text-xs pointer-events-none">%</span>
                                </div>
                            </div>
                            <div className="flex justify-between label-ui">
                                <span>{t('herrEst.anticipo.alFirmar')}</span>
                                <span>{t('estimado.anticipo.saldo')} <span className="font-data">{formatCurrency(Math.max(0, totals.grandTotal - totals.depositAmount))}</span> {t('estimado.anticipo.alterminar')}</span>
                            </div>
                        </div>

                        {/* Fees Config */}
                        <div className="space-y-3">
                            <h4 className="label-ui">{t('herrEst.cargos.titulo')}</h4>

                            <label className="flex items-center justify-between p-4 bg-white border border-acero-200 rounded-2xl cursor-pointer hover:border-acero-300 transition-colors duration-150">
                                <span className="text-sm font-medium text-navy-950">{t('herrEst.cargos.viaje')}<span className="font-data tabular-nums">{formatCurrency(totals.tripFee)}</span>)</span>
                                <input
                                    type="checkbox"
                                    className="w-5 h-5 rounded accent-navy-850 border-acero-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                    checked={adjustments.tripCharge}
                                    onChange={(e) => setAdjustments({ ...adjustments, tripCharge: e.target.checked })}
                                />
                            </label>

                            <div className="p-4 bg-white border border-acero-200 rounded-2xl">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-navy-950">{t('herrEst.impuesto.titulo')}</span>
                                    <input
                                        type="checkbox"
                                        aria-label={t('estimado.impuesto.aplicararia')}
                                        className="w-5 h-5 rounded accent-navy-850 border-acero-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                        checked={adjustments.applyTax}
                                        onChange={(e) => setAdjustments({ ...adjustments, applyTax: e.target.checked })}
                                    />
                                </div>
                                {adjustments.applyTax && (
                                    <div className="space-y-3 mt-3 pt-3 border-t border-acero-100 animate-in slide-in-from-top-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-acero-600">{t('herrEst.impuesto.tasa')}</span>
                                            <input
                                                type="number"
                                                aria-label={t('estimado.impuesto.tasaaria')}
                                                className="w-20 h-10 px-2 text-xs bg-white border border-acero-300 rounded-xl text-right font-data tabular-nums text-navy-950 transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                                value={adjustments.taxRate}
                                                onChange={(e) => setAdjustments({ ...adjustments, taxRate: Number(e.target.value) })}
                                            />
                                        </div>
                                        <label className="flex items-center justify-between text-xs text-acero-600 cursor-pointer">
                                            <span>{t('herrEst.impuesto.manoDeObra')}</span>
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded accent-navy-850 border-acero-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                                checked={adjustments.taxLabor}
                                                onChange={(e) => setAdjustments({ ...adjustments, taxLabor: e.target.checked })}
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default EstimateToolPanel;
